// APP_DISPATCH table — flat URL → page lookup. First match wins, ordered
// most-specific to most-general.
//
// Sub-app pages live in the unified `pages` map (built in context.js) and
// the smbls auto-router mounts the matched page directly into Slot via
// `customRouterElement: ['Slot']`. Cross-cutting chrome (AppAssistant,
// ChatDrawer, InstallableModalStub) lives on the shell's app.js so it's
// available regardless of which sub-app's page is active.
//
// `page:` entries here cover shell-direct surfaces (auth/account/admin)
// that aren't in any sub-app's pages map.

export const APP_DISPATCH = [
  // Shell-direct pages — these aren't in sub-app pages maps because
  // they're shared chrome surfaces (login, account settings, admin).
  { match: ['/login', '/signup', '/forgot-password', '/verify-email'], page: 'auth' },
  { match: ['/profile', '/orgs'], page: 'account' },
  { match: /^\/account(\/|$)/, page: 'account' },
  { match: /^\/admin(\/|$)/, page: 'admin' },
  // WORKSPACE-ROUTING-PROJECTS-404: `/projects` previously mapped to a
  // bogus `profile` import that rendered the user profile page (and
  // titled the tab "Not found / Symbols.app" once the auto-router fell
  // through). The canonical projects list lives at `/data/projects` —
  // redirect there so any leftover `/projects` link reaches the right
  // surface. `/workspaces` had the same wrong-page bug; collapse both
  // onto `/data/projects` (workspace = the canonical org-level
  // container, projects = the rows shown in that view).
  { match: ['/projects', '/workspaces'], redirect: '/data/projects' }
]

/**
 * Match a URL path against the dispatch table.
 *
 * @param {string} path
 * @param {Array}  table - defaults to APP_DISPATCH
 * @returns {object|null} matched entry or null
 */
export const matchDispatch = (path, table = APP_DISPATCH) => {
  for (const entry of table) {
    const m = entry.match
    if (Array.isArray(m) && m.includes(path)) return entry
    if (typeof m === 'string' && m === path) return entry
    if (m instanceof RegExp && m.test(path)) return entry
  }
  return null
}

// Use only the named exports each module actually has — Parcel's static
// analysis on dynamic-import .then(m => m.X) fails the build when X
// doesn't exist (e.g. login.js has no `default` export).
//
// auth/account dynamic-import the BARE `@symbo-ls/workspace-shared` root,
// not a deep pages/auth/login.js or pages/account/index.js subpath — a
// per-file deep specifier into @symbo-ls/workspace-shared (a restricted/
// private-registry package) cannot resolve through the platform's public
// importmap once this package is pushed as a library (tickets/execution-
// board.md §1 P0). The bare root IS resolvable (same pattern sharedLibraries.js
// already uses) and this stays a genuinely lazy chunk either way — only the
// specifier changed, not the dynamic-import shape.
export const PAGE_IMPORTS = {
  auth: () => import('@symbo-ls/workspace-shared').then((m) => m.default.pages.login),
  account: () => import('@symbo-ls/workspace-shared').then((m) => m.default.pages.account),
  admin: () => import('./pages/admin/index.js').then((m) => m.admin)
}

/**
 * Dispatch a URL to the shell's Slot element.
 *
 * @param {object} shell - the root shell element returned by create()
 * @param {string} path  - current URL path
 * @returns {Promise<void>}
 */
export const dispatchUrl = async (shell, path) => {
  const target = matchDispatch(path)
  if (!target) return

  if (target.redirect) {
    // replaceState alone doesn't trigger smbls's auto-router; dispatch a
    // synthetic popstate so it re-evaluates against the canonical path.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      try {
        window.history.replaceState(null, '', target.redirect)
      } catch (_) {}
      try {
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch (_) {}
    }
    return
  }

  if (target.page) {
    const importFn = PAGE_IMPORTS[target.page]
    if (importFn) {
      const page = await importFn().catch(() => null)
      if (page && shell.Slot) shell.Slot.set?.(page)
    }
  }
}
